import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { cn } from 'components/lib/utils';

const DEBOUNCE_MS = 300;

interface ISearchFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const SearchFilter = memo(
  ({
    value,
    onChange,
    placeholder = 'Search by Name or External ID',
    className
  }: ISearchFilterProps) => {
    const [local, setLocal] = useState(value);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
      setLocal(value);
    }, [value]);

    useEffect(() => {
      if (local === value) return;
      const handle = window.setTimeout(() => {
        onChangeRef.current(local);
      }, DEBOUNCE_MS);
      return () => window.clearTimeout(handle);
    }, [local, value]);

    const handleClear = useCallback((): void => {
      setLocal('');
      onChangeRef.current('');
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
          onChangeRef.current(local);
        }
      },
      [local]
    );

    return (
      <div
        className={cn(
          'relative flex h-8 w-[260px] items-center rounded-md border border-base-input bg-base-background',
          className
        )}
        data-testid="offers-search-filter"
      >
        <div className="pl-3 text-base-mutedForeground">
          <FontAwesomeIcon icon="fa-regular fa-magnifying-glass" className="h-3.5 w-3.5" />
        </div>
        <input
          type="text"
          value={local}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setLocal(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-full flex-1 bg-transparent px-2 text-p-s text-base-foreground placeholder:text-base-mutedForeground focus:outline-none"
        />
        {local && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="mr-2 flex h-4 w-4 items-center justify-center rounded-full text-base-mutedForeground hover:text-base-foreground"
            data-testid="offers-search-filter-clear"
          >
            <FontAwesomeIcon icon="fa-regular fa-circle-xmark" className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }
);

SearchFilter.displayName = 'SearchFilter';
